import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ReactComponent as CloseIcon } from 'applicaiton/assets/close.svg';
import { ReactComponent as AddIcon } from 'applicaiton/assets/add.svg';
import Switch from 'common/components/Switch';
import TimePicker from 'common/components/TimePicker';
import { TimeWrapper, Wrapper, DividerStyled } from './styles';
import { TimeRangeProps } from './modules';
import { TimeSlots } from 'common/types/clinics';

//this value using to set one day for all slots
const defaultDate = '2000-01-01T';

/**
 * Represents a time range component that displays and manages multiple time slots.
 *
 * @param {TimeRangeProps} props - The props for the TimeRange component.
 * @returns {JSX.Element} A JSX element representing the TimeRange component.
 */
const TimeRange: FC<TimeRangeProps> = ({
  day,
  slots,
  isEnabled,
  handleEditSlot,
  handleAddSlot,
  handleRemoveSlot,
  handleEnableSlot,
  hasError,
}) => {
  const { t } = useTranslation();

  /**
   * Calculates the minimum start time for a given slot based on its index and previous slots.
   *
   * @param {string} startTime - The current start time of the slot.
   * @param {number} index - The index of the current slot.
   * @returns {dayjs.Dayjs | null} The calculated minimum start time or null if not applicable.
   */
  const getMinStartTime = (startTime: string, index: number) => {
    if (!slots.length || index === 0) return null;

    return dayjs(
      !startTime
        ? `${dayjs().format('YYYY-MM-DD')}T` + slots[index - 1].endTime
        : defaultDate + slots[index - 1].endTime,
    );
  };

  /**
   * Calculates the minimum end time for a given slot based on its start time.
   *
   * @param {string} startTime - The current start time of the slot.
   * @returns {dayjs.Dayjs} The calculated minimum end time.
   */
  const getMinEndTime = (startTime: string) => {
    const incrementedTime = dayjs(startTime).minute() + 5;
    return dayjs(startTime).set('minutes', incrementedTime);
  };

  /**
   * Checks if a given value is invalid for a specific slot based on its index and time.
   *
   * @param {string} currentDay - The day associated with the slot.
   * @param {number} index - The index of the current slot.
   * @param {string} time - The time to validate.
   * @returns {boolean} True if the value is invalid, false otherwise.
   */
  const isInvalidValue = (currentDay: string, index: number, time: string) => {
    if (day !== currentDay || !time) return false;
    return slots.some((slot, i) => {
      if (i !== index) {
        return (slot.startTime || '') < time && (slot.endTime || '') > time;
      }
      return false;
    });
  };

  /**
   * Handles the change of a slot's start or end time.
   *
   * @param {string | null} value - The new value for the slot's property.
   * @param {number} index - The index of the slot being edited.
   * @param {keyof TimeSlots} key - The property of the slot being edited (either 'startTime' or 'endTime').
   */
  const handleChange = (
    value: string | null,
    index: number,
    key: keyof TimeSlots,
  ) => {
    handleEditSlot(day, index, {
      [key]: value ? dayjs(value).format('HH:mm') : '',
    });
  };

  return (
    <Wrapper>
      <h2>{t(day.toLowerCase())}</h2>
      <Switch
        checked={isEnabled}
        onChange={(value) => handleEnableSlot(day, value)}
      />
      <div>
        {slots.map((item, index) => (
          <TimeWrapper key={index}>
            <TimePicker
              defaultValue={dayjs(defaultDate + item.startTime)}
              value={
                item.startTime ? dayjs(defaultDate + item.startTime) : null
              }
              onChange={(value) => handleChange(value, index, 'startTime')}
              minTime={getMinStartTime(item.startTime, index)}
              maxTime={dayjs(defaultDate).hour(23).minute(55)}
              disabled={!isEnabled}
              isError={
                isInvalidValue(day, index, item.startTime!) ||
                (!item.startTime && !!hasError)
              }
            />
            <DividerStyled />
            <TimePicker
              defaultValue={dayjs(defaultDate + item.endTime)}
              value={item.endTime ? dayjs(defaultDate + item.endTime) : null}
              minTime={getMinEndTime(defaultDate + item.startTime)}
              maxTime={dayjs(defaultDate).hour(23).minute(59)}
              onChange={(value) => handleChange(value, index, 'endTime')}
              disabled={!item.startTime || !isEnabled}
              isError={
                isInvalidValue(day, index, item.endTime!) ||
                (!item.endTime && !!hasError)
              }
            />
            {index ? (
              <button
                onClick={() => handleRemoveSlot(day, index)}
                disabled={!isEnabled}
              >
                <CloseIcon />
              </button>
            ) : (
              ''
            )}
          </TimeWrapper>
        ))}
        <button
          disabled={!isEnabled}
          type="button"
          onClick={() => handleAddSlot(day)}
        >
          <AddIcon />
          <h2>{t('add_another')}</h2>
        </button>
      </div>
    </Wrapper>
  );
};

export default TimeRange;
